import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api.ts'
import {
  Monitor, Shield, Archive, Mail, CheckCircle2,
  AlertTriangle, XCircle, Wifi, WifiOff,
} from 'lucide-react'
import { Card, PageHeader, Skeleton } from '@/components/ui'
import { cn } from '@/lib/utils.ts'

interface CwStats { totalComputers: number; onlineComputers: number; offlineComputers: number }
interface EsetStats { totalLicenses: number; expiringLicenses: number; expiredLicenses: number; protectedDevices: number; activeThreats: number }
interface MailstoreStats { totalMessages: number; totalSize: number; users: number; lastArchivingAt?: string }
interface NspSummary { todayIn: number; todayOut: number; todayBlocked: number; quarantineCount: number; blockedSenders: number }

type TileTone = 'brand' | 'success' | 'danger' | 'warning' | 'neutral' | 'accent'

const TILE_TONE_CLASSES: Record<TileTone, { bg: string; icon: string }> = {
  brand: { bg: 'bg-brand-50', icon: 'text-brand-600' },
  success: { bg: 'bg-green-50', icon: 'text-green-600' },
  danger: { bg: 'bg-red-50', icon: 'text-red-600' },
  warning: { bg: 'bg-yellow-50', icon: 'text-yellow-600' },
  neutral: { bg: 'bg-gray-100', icon: 'text-gray-500' },
  accent: { bg: 'bg-accent-50', icon: 'text-accent-600' },
}

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B'
  const gb = bytes / (1024 ** 3)
  if (gb >= 1) return `${gb.toFixed(1)} GB`
  const mb = bytes / (1024 ** 2)
  return `${mb.toFixed(0)} MB`
}

export function MonitoringPage() {
  const cwStats = useQuery<CwStats>({
    queryKey: ['cw-stats'],
    queryFn: () => api.get('/monitoring/cw/stats').then((r) => r.data),
    retry: false,
  })

  const esetStats = useQuery<EsetStats>({
    queryKey: ['eset-stats'],
    queryFn: () => api.get('/eset/stats').then((r) => r.data),
    retry: false,
  })

  const mailstoreStats = useQuery<MailstoreStats>({
    queryKey: ['mailstore-stats'],
    queryFn: () => api.get('/monitoring/mailstore/stats').then((r) => r.data),
    retry: false,
  })

  const nspSummary = useQuery<NspSummary>({
    queryKey: ['nsp-summary'],
    queryFn: () => api.get('/monitoring/nsp/summary').then((r) => r.data),
    retry: false,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Monitoring"
        description="ConnectWise Automate · ESET MSP · MailStore · NoSpamProxy"
      />

      <MonitoringSection
        icon={<Monitor className="h-5 w-5 text-brand-600" />}
        title="ConnectWise Automate"
        gridCols="grid-cols-1 sm:grid-cols-3"
      >
        <StatTile
          label="Geräte gesamt"
          value={cwStats.data?.totalComputers ?? '—'}
          loading={cwStats.isLoading}
          icon={<Monitor className="h-5 w-5" />}
          tone="brand"
        />
        <StatTile
          label="Online"
          value={cwStats.data?.onlineComputers ?? '—'}
          loading={cwStats.isLoading}
          icon={<Wifi className="h-5 w-5" />}
          tone="success"
        />
        <StatTile
          label="Offline"
          value={cwStats.data?.offlineComputers ?? '—'}
          loading={cwStats.isLoading}
          icon={<WifiOff className="h-5 w-5" />}
          tone={cwStats.data && cwStats.data.offlineComputers > 0 ? 'danger' : 'neutral'}
        />
      </MonitoringSection>

      <MonitoringSection
        icon={<Shield className="h-5 w-5 text-green-600" />}
        title="ESET MSP"
        gridCols="grid-cols-2 lg:grid-cols-4"
      >
        <StatTile
          label="Lizenzen"
          value={esetStats.data?.totalLicenses ?? '—'}
          loading={esetStats.isLoading}
          icon={<CheckCircle2 className="h-5 w-5" />}
          tone="success"
        />
        <StatTile
          label="Ablaufend (30T)"
          value={esetStats.data?.expiringLicenses ?? '—'}
          loading={esetStats.isLoading}
          icon={<AlertTriangle className="h-5 w-5" />}
          tone={esetStats.data && esetStats.data.expiringLicenses > 0 ? 'warning' : 'neutral'}
        />
        <StatTile
          label="Geschützte Geräte"
          value={esetStats.data?.protectedDevices ?? '—'}
          loading={esetStats.isLoading}
          icon={<Shield className="h-5 w-5" />}
          tone="success"
        />
        <StatTile
          label="Aktive Bedrohungen"
          value={esetStats.data?.activeThreats ?? '—'}
          loading={esetStats.isLoading}
          icon={<XCircle className="h-5 w-5" />}
          tone={esetStats.data && esetStats.data.activeThreats > 0 ? 'danger' : 'neutral'}
        />
      </MonitoringSection>

      <MonitoringSection
        icon={<Archive className="h-5 w-5 text-brand-500" />}
        title="MailStore"
        gridCols="grid-cols-1 sm:grid-cols-3"
      >
        <StatTile
          label="Archivierte E-Mails"
          value={mailstoreStats.data ? mailstoreStats.data.totalMessages.toLocaleString('de') : '—'}
          loading={mailstoreStats.isLoading}
          icon={<Archive className="h-5 w-5" />}
          tone="brand"
        />
        <StatTile
          label="Archivgröße"
          value={mailstoreStats.data ? formatBytes(mailstoreStats.data.totalSize) : '—'}
          loading={mailstoreStats.isLoading}
          icon={<Archive className="h-5 w-5" />}
          tone="brand"
        />
        <StatTile
          label="Benutzer"
          value={mailstoreStats.data?.users ?? '—'}
          loading={mailstoreStats.isLoading}
          icon={<Archive className="h-5 w-5" />}
          tone="brand"
        />
      </MonitoringSection>

      <MonitoringSection
        icon={<Mail className="h-5 w-5 text-accent-600" />}
        title="NoSpamProxy (heute)"
        gridCols="grid-cols-2 lg:grid-cols-5"
      >
        <StatTile
          label="Eingehend"
          value={nspSummary.data?.todayIn ?? '—'}
          loading={nspSummary.isLoading}
          icon={<Mail className="h-5 w-5" />}
          tone="brand"
        />
        <StatTile
          label="Ausgehend"
          value={nspSummary.data?.todayOut ?? '—'}
          loading={nspSummary.isLoading}
          icon={<Mail className="h-5 w-5" />}
          tone="brand"
        />
        <StatTile
          label="Blockiert"
          value={nspSummary.data?.todayBlocked ?? '—'}
          loading={nspSummary.isLoading}
          icon={<XCircle className="h-5 w-5" />}
          tone={nspSummary.data && nspSummary.data.todayBlocked > 0 ? 'danger' : 'neutral'}
        />
        <StatTile
          label="Quarantäne"
          value={nspSummary.data?.quarantineCount ?? '—'}
          loading={nspSummary.isLoading}
          icon={<AlertTriangle className="h-5 w-5" />}
          tone={nspSummary.data && nspSummary.data.quarantineCount > 0 ? 'warning' : 'neutral'}
        />
        <StatTile
          label="Blockliste"
          value={nspSummary.data?.blockedSenders ?? '—'}
          loading={nspSummary.isLoading}
          icon={<XCircle className="h-5 w-5" />}
          tone="neutral"
        />
      </MonitoringSection>
    </div>
  )
}

function MonitoringSection({
  icon,
  title,
  gridCols,
  children,
}: {
  icon: ReactNode
  title: string
  gridCols: string
  children: ReactNode
}) {
  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          {icon}
          <Card.Title>{title}</Card.Title>
        </div>
      </Card.Header>
      <Card.Body>
        <div className={cn('grid gap-3', gridCols)}>{children}</div>
      </Card.Body>
    </Card>
  )
}

function StatTile({
  label,
  value,
  loading,
  icon,
  tone = 'brand',
}: {
  label: string
  value: string | number
  loading?: boolean
  icon: ReactNode
  tone?: TileTone
}) {
  const tones = TILE_TONE_CLASSES[tone]
  return (
    <Card className="shadow-none">
      <Card.Body className="flex items-center gap-3">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-md',
            tones.bg,
            tones.icon,
          )}
        >
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-caption text-gray-500">{label}</p>
          {loading ? (
            <Skeleton className="mt-1 h-5 w-12" />
          ) : (
            <p className="text-h4 text-gray-900">{value}</p>
          )}
        </div>
      </Card.Body>
    </Card>
  )
}
